import { useEffect, useRef, useState } from "react";
import { Api } from "./client.js";

export interface ErpFreshness {
  /** The company mirrors an ERP at all (a duty is switched on). */
  mirrored: boolean;
  lastRunAt: string | null;
  /** A run is asked for and not yet reported. */
  pending: boolean;
}

/**
 * Nobody waits on the mirror's schedule (owner 2026-10-30): when a list of orders opens, ask the
 * ERP mirror to run now and, once a run has finished after the request, call `onFresh` so the
 * list reloads. Returns what the screen may show: "uppdaterat HH:MM" or "hämtar ändringar".
 */
export function useErpRefresh(what: string, onFresh: () => void, enabled = true): ErpFreshness | null {
  const [status, setStatus] = useState<ErpFreshness | null>(null);
  const fresh = useRef(onFresh);
  fresh.current = onFresh;
  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = async (): Promise<ErpFreshness> => {
      const duties = (await Api.syncStatus()).filter((d) => d.enabled);
      return { mirrored: duties.length > 0, lastRunAt: duties.map((d) => d.lastRunAt ?? "").sort().at(-1) || null, pending: duties.some((d) => d.pending) };
    };
    void (async () => {
      try {
        const before = await read();
        if (stop) return;
        setStatus(before);
        if (!before.mirrored) return;
        await Api.syncRefresh(what);
        setStatus({ ...before, pending: true });
        const started = Date.now();
        const poll = async () => {
          if (stop) return;
          const now = await read().catch(() => null);
          if (stop) return;
          if (now) {
            setStatus(now);
            if (now.lastRunAt && now.lastRunAt !== before.lastRunAt) { fresh.current(); return; }
          }
          if (Date.now() - started < 120_000) timer = setTimeout(() => void poll(), 3000);
        };
        timer = setTimeout(() => void poll(), 3000);
      } catch {
        // No mirror or no network: the list is what it is.
      }
    })();
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [what, enabled]);
  return status;
}

/** The one-line label for a freshness state, or null when there is nothing to say. */
export function erpLabel(s: ErpFreshness | null, t: (k: "erp.refreshing" | "erp.updated", v?: Record<string, string | number>) => string): string | null {
  if (!s?.mirrored) return null;
  if (s.pending) return t("erp.refreshing");
  if (!s.lastRunAt) return null;
  return t("erp.updated", { time: new Date(s.lastRunAt).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }) });
}

/**
 * Read one order from the ERP now and wait for it (owner 2026-10-30: operators are used to a few
 * seconds while an order loads; the order is then current). Resolves when a run has reported
 * after the request, or at the timeout; `fresh` says which. A company without a mirror resolves
 * at once.
 */
export async function refreshOrderNow(what: "sales-orders" | "receipts", reference: string, timeoutMs = 25_000): Promise<{ mirrored: boolean; fresh: boolean }> {
  let before: string | null;
  try {
    const duties = (await Api.syncStatus()).filter((d) => d.enabled);
    if (duties.length === 0) return { mirrored: false, fresh: false };
    before = duties.map((d) => d.lastRunAt ?? "").sort().at(-1) || null;
    await Api.syncRefresh(what, [reference]);
  } catch {
    return { mirrored: false, fresh: false };
  }
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    await new Promise((r) => setTimeout(r, 1500));
    try {
      const duties = (await Api.syncStatus()).filter((d) => d.enabled);
      const now = duties.map((d) => d.lastRunAt ?? "").sort().at(-1) || null;
      if (now && now !== before && !duties.some((d) => d.pending)) return { mirrored: true, fresh: true };
    } catch {
      // keep waiting
    }
  }
  return { mirrored: true, fresh: false };
}

/**
 * While an order is being worked on: call `onRun` each time the mirror has reported a new run,
 * so the screen can re-read the order and say when the ERP changed it. Quiet when the company
 * has no mirror.
 */
export function useErpRuns(enabled: boolean, onRun: () => void, everyMs = 20_000): void {
  const cb = useRef(onRun);
  cb.current = onRun;
  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    let last: string | null | undefined;
    const tick = async () => {
      try {
        const duties = (await Api.syncStatus()).filter((d) => d.enabled);
        if (duties.length === 0) return;
        const now = duties.map((d) => d.lastRunAt ?? "").sort().at(-1) || null;
        if (last === undefined) last = now;
        else if (now && now !== last) { last = now; if (!stop) cb.current(); }
      } catch {
        // offline: nothing to say
      }
    };
    void tick();
    const timer = setInterval(() => void tick(), everyMs);
    return () => { stop = true; clearInterval(timer); };
  }, [enabled, everyMs]);
}

